import classNames from 'classnames';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Icon, { Icons } from '../../components/Icon/Icon';
import { useConstHandler } from '../../hooks/useConstFunction';
import RootModalModel from '../../models/root-modal';
import HelperService from '../../services/HelperService';
import { appSlice } from '../../store/slices';
import './RootModal.scss';

const SWIPE_THRESHOLD = 70;

const getViewportH = () =>
	(window.visualViewport && window.visualViewport.height) ||
	window.innerHeight ||
	document.documentElement.clientHeight ||
	0;

const parseHeightSpec = h => {
	if (h == null) return null;
	if (typeof h === 'number') return { kind: 'px', px: h };
	const s = String(h).trim().toLowerCase();
	if (s.endsWith('px')) return { kind: 'px', px: parseFloat(s) };
	if (s.endsWith('%')) return { kind: 'percent', ratio: parseFloat(s) / 100 };
	if (s.endsWith('vh') || s.endsWith('dvh') || s.endsWith('svh') || s.endsWith('lvh')) {
		return { kind: 'percent', ratio: parseFloat(s) / 100 };
	}
	const num = Number(s);
	if (!Number.isNaN(num)) return { kind: 'px', px: num };
	return null;
};

const specToPx = spec => {
	if (!spec) return null;
	if (spec.kind === 'px') return Math.round(spec.px);
	if (spec.kind === 'percent') return Math.round(getViewportH() * spec.ratio);
	return null;
};

const RootModal = ({ modal, bodyComponent: BodyComponent, footerComponent: FooterComponent }) => {
	const dispatch = useDispatch();

	const [fullScreen, setFullScreen] = useState(false);
	const [openAnimate, setOpenAnimate] = useState(false);
	const [renderModal, setRenderModal] = useState(false);
	const swiperRef = useRef(null);
	const [startY, setStartY] = useState();
	const [isDraggingMouse, setIsDraggingMouse] = useState(false);
	const [initialSpec, setInitialSpec] = useState(null);
	const [dragging, setDragging] = useState(false);
	const [initialHeight, setInitialHeight] = useState(null);
	const [dragBaseHeight, setDragBaseHeight] = useState(null);
	const [wasFullScreenAtStart, setWasFullScreenAtStart] = useState(false);

	const [heightBody, setHeightBody] = useState();
	const [dragHeight, setDragHeight] = useState(null);

	const {
		id,
		headClass,
		title,
		subtitle,
		tag,
		isOpen,
		fullScreenBase,
		height,
		headerContent,
		headerBackground,
		leftButtons,
		buttonStyles,
		isHeaderWithColorBgAndIcon,
		headerIcon,
		headerBackgroundColor,
		outsideParams,
	} = useMemo(() => new RootModalModel(modal), [modal]); // todo: RootModalModel скорее всего надо будет заменить на простое присвоение значений по умолчании

	const handleClose = useConstHandler(() => {
		dispatch(appSlice.actions.closeLastModal());
		if (fullScreen) {
			setFullScreen(false);
		}
	});

	const startDrag = e => {
		if (fullScreenBase) return;

		const y = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
		setStartY(y);
		setDragging(true);
		setWasFullScreenAtStart(fullScreen);

		const elH = swiperRef.current?.getBoundingClientReact?.().height ?? swiperRef.current?.offsetHeight ?? 0;
		setDragBaseHeight(elH);

		if (!fullScreen) {
			if (heightBody === null) setHeightBody(initialHeight ?? elH);
		}
	};

	const moveDrag = e => {
		if (!dragging || fullScreenBase) return;

		const y = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
		const delta = startY - y;
		const base =
			dragBaseHeight ?? swiperRef.current?.getBoundingClientReact?.().height ?? swiperRef.current?.offsetHeight ?? 0;

		let next = base + delta;

		if (initialHeight != null) next = Math.max(next, initialHeight);
		setDragHeight(next);
	};

	const endDrag = e => {
		if (!dragging || fullScreenBase) return;
		setDragging(false);

		const y = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
		const delta = startY - y;

		setDragHeight(null);

		if (delta > SWIPE_THRESHOLD) {
			setFullScreen(true);
			setHeightBody(null);
		} else if (delta < -SWIPE_THRESHOLD) {
			setFullScreen(false);
			setHeightBody(initialHeight);
		} else {
			if (wasFullScreenAtStart) {
				setFullScreen(true);
				setHeightBody(null);
			} else {
				setFullScreen(false);
				setHeightBody(initialHeight);
			}
		}
	};

	useEffect(() => {
		if (isOpen) {
			setRenderModal(true);
			setTimeout(() => setOpenAnimate(true), 30);
		} else {
			setOpenAnimate(false);
			setTimeout(() => setRenderModal(false), 400);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) {
			setTimeout(() => document.body.classList.add('no-scroll'), 600);
		} else {
			document.body.classList.remove('no-scroll');
		}
	}, [isOpen]);

	useEffect(() => {
		if (!fullScreen) setFullScreen(fullScreenBase);
	}, [fullScreenBase]);

	useEffect(() => {
		if (!isOpen) {
			setInitialSpec(null);
			setInitialHeight(null);
			setHeightBody(null);
			setFullScreen(null);
			return;
		}

		const initHeight = async () => {
			await HelperService.Sleep(0);

			const spec = parseHeightSpec(height);
			if (spec) {
				const px = specToPx(spec);
				setInitialSpec(spec);
				setInitialHeight(px);
				setHeightBody(px);
				setFullScreen(false);
			} else {
				const h = swiperRef.current?.getBoundingClientReact?.().height ?? swiperRef.current?.offsetHeight ?? 0;
				setInitialSpec({ kind: 'px', px: h });
				setInitialHeight(h);
				setHeightBody(h);
				setFullScreen(false);
			}
		};

		initHeight();
	}, [isOpen, height]);

	useEffect(() => {
		if (!isOpen) return;
		if (!initialSpec || initialSpec !== 'percent') return;

		const recalc = () => {
			const px = specToPx(initialSpec);
			setInitialHeight(px);
			if (!fullScreen && !dragging) setHeightBody(px);
		};

		window.addEventListener('resize', recalc);
		window.visualViewport?.removeEventListener?.('resize', recalc);
		return () => {
			window.removeEventListener('resize', recalc);
			window.visualViewport?.removeEventListener?.('resize', recalc);
		};
	}, [isOpen, initialSpec, fullScreen, dragging]);

	const isCustomHeader = !!headerContent;

	return (
		renderModal && (
			<div
				id='RootModal'
				className={classNames(headClass ? headClass : '', { 'full-screen': fullScreen, open: openAnimate })}
				onMouseMove={moveDrag}
				onMouseLeave={() => dragging && setDragging(false)}
			>
				<div className='RootModal-bg' onClick={() => handleClose()}></div>

				{isHeaderWithColorBgAndIcon ? (
					<div
						className={'RootModal-header'}
						onTouchStart={startDrag}
						onTouchMove={moveDrag}
						onTouchEnd={endDrag}
						onMouseDown={startDrag}
						onMouseUp={endDrag}
						style={{
							position: 'relative',
							borderRadius: '24px 24px 0 0',
							background: headerBackgroundColor,
						}}
					>
						<div
							className='RootModal-grabber'
							aria-label='true'
							style={{ background: 'rgba(var(--bg-secondary), 0.3)' }}
						/>
						<div
							className={`RootModal-header-close`}
							style={{
								position: 'absolute',
								top: '12px',
								right: '16px',
								'--root-modal-icon-color': 'rgba(var(--bg-secondary), 0.6)',
								background: 'rgba(var(--bg-secondary), 0.2)',
								height: '32px',
								width: '32px',
							}}
						>
							<Icon
								type={Icons.XMARK_BOLD}
								width={32}
								height={32}
								onClick={() => handleClose()}
								style={{ fill: 'rgba(var(--label-secondary), 0.6)' }}
							/>
						</div>
						<div className='headerWithColorBgAndIcon'>
							<div className='headerWithColorBgAndIcon_text'>
								<p className='headerWithColorBgAndIcon_text_title'>{title}</p>
								<p className='headerWithColorBgAndIcon_text_subtitle'>{subtitle}</p>
							</div>
							<div className='headerWithColorBgAndIcon_icon'>
								<Icon type={Icons[headerIcon]} width={108} height={108} />
							</div>
						</div>
					</div>
				) : (
					<div
						className={classNames('RootModal-header')}
						onTouchStart={startDrag}
						onTouchMove={moveDrag}
						onTouchEnd={endDrag}
						onMouseDown={startDrag}
						onMouseUp={endDrag}
						style={{
							borderRadius: fullScreen ? '24px 24px 0 0' : '24px 24px 0 0',
							background: isCustomHeader ? headerBackground || 'transparent' : 'rgb(var(--bg-primary))',
						}}
					>
						{!fullScreenBase && (
							<div
								className='RootModal-grabber'
								aria-label='true'
								style={{
									background: buttonStyles ? buttonStyles?.color : '',
								}}
							/>
						)}

						<div className={`RootModal-header-row ${headerContent ? 'custom' : 'default'}`}>
							<div
								className='RootModal-header-left'
								style={headerContent && typeof headerContent !== 'string' ? { top: '0', position: 'absolute' } : {}}
							>
								{Array.isArray(leftButtons) &&
									leftButtons.map(
										(btn, index) =>
											btn.icon && (
												<div
													key={index}
													className='RootModal-header-left-btn'
													style={{
														'--root-modal-icon-color': buttonStyles?.color || 'rgba(var(--label-secondary), 0.6)',
														background: buttonStyles.background ? buttonStyles?.background : '',
													}}
												>
													<Icon
														type={btn.icon}
														width={16}
														height={16}
														onClick={btn.onClick}
														style={{
															fill: buttonStyles?.color ? buttonStyles.color : '',
														}}
													/>
												</div>
											)
									)}
							</div>

							{!isCustomHeader ? (
								<div className={classNames('RootModal-header-title')}>{title}</div>
							) : (
								<>{headerContent}</>
							)}
							<div
								className={`RootModal-header-close`}
								style={{
									'--root-modal-icon-color': buttonStyles?.color || 'rgba(var(--label-secondary), 0.6)',
									...(headerContent && typeof headerContent !== 'string' ? { top: '0', position: 'absolute' } : {}),
									background: buttonStyles?.background ? buttonStyles?.background : '',
								}}
							>
								<Icon
									type={Icons.XMARK_BOLD}
									width={16}
									height={16}
									onClick={() => handleClose()}
									style={{ fill: buttonStyles?.color ? buttonStyles.color : 'rgba(var(--label-secondary), 0.6)' }}
								/>
							</div>
						</div>
					</div>
				)}

				<div
					ref={swiperRef}
					className={classNames('RootModal-body', { animate: !isDraggingMouse })}
					style={{ height: dragHeight ?? heightBody }}
				>
					<BodyComponent tag={tag} id={id} outsideParams={outsideParams} handleClose={handleClose} />
				</div>
				{FooterComponent ? (
					<div className={`RootModal-footer`}>
						<FooterComponent tag={tag} id={id} outsideParams={outsideParams} handleClose={handleClose} />
					</div>
				) : null}
			</div>
		)
	);
};

export function createRootModal(BodyComponent, { config, footerComponent } = {}) {
	return memo(({ modal: modalOverride }) => {
		const modal = useMemo(() => ({ ...config, ...modalOverride }), [modalOverride]);
		return <RootModal modal={modal} bodyComponent={BodyComponent} footerComponent={footerComponent} />;
	});
}

export default RootModal;
